import { useQuery } from "@tanstack/react-query";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import {
  resolveOrderLoadStatus,
  type HistoricalLoadStatus,
} from "@/lib/order-load-status";
import {
  ArrowUpRight,
  ArrowDownRight,
  Container,
  FileText,
  Loader2,
  Pencil,
  Repeat,
  Truck,
  User,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataCard } from "@/components/ui/data-card";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StarOrderButton } from "@/components/orders/StarOrderButton";
import { InlineAssignCell } from "@/components/orders/InlineAssignCell";
import { InlineEditCell } from "@/components/orders/InlineEditCell";
import { InlineTrailerTypeCell } from "@/components/orders/InlineTrailerTypeCell";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  mapAndHydrateLegRows,
} from "@/lib/orders-with-legs";
import { isLateArrival, isLateDeparture, formatTimeWithTz, formatWindowCompact } from "@/lib/order-time";
import type { Order, Customer, Driver, Location, Tractor } from "@/types/tms";
import { resolveOrderEndpoints } from "@/lib/order-endpoints";
import { TrailerArrivedNotice } from "./TrailerArrivedNotice";
import { toLegsWithArrivedTrailer } from "@/lib/order-trailer-arrival";

export type OrderCardOrder = Order & {
  billing_party: Customer | null;
  assigned_tractor: Tractor | null;
  assigned_operator: Driver | null;
};

interface OrderCardProps {
  order: OrderCardOrder;
  selected: boolean;
  isChecked: boolean;
  onToggle: (id: string) => void;
  onClick: (order: OrderCardOrder) => void;
  onEdit: (order: Order) => void;
  loadStatuses?: Record<
    string,
    { load_status: string | null; trailer_type_id: string | null; location_status?: string | null }
  >;
  /** At-delivery load-status snapshots keyed by order id. */
  loadStatusAtDelivery?: Map<string, HistoricalLoadStatus>;
  locations?: Location[];
  /** Leg statuses for the rollup chip when the caller loads them separately
   *  (the orders list attaches them to the order itself). */
  legs?: Order["legs"];
}

/**
 * Mobile/tablet representation of a parent order row. Mirrors the columns
 * exposed by `ExpandableParentRow` in `OrderListView` so both views stay
 * functionally identical at the `md` breakpoint.
 */
export function OrderCard({
  order,
  selected,
  isChecked,
  onToggle,
  onClick,
  onEdit,
  loadStatuses,
  loadStatusAtDelivery,
  locations,
  legs,
}: OrderCardProps) {
  const viewerTz = useViewerTimezone();
  const lateDep = isLateDeparture(order);
  const lateArr = isLateArrival(order);
  const { isReadOnly } = useUserRole();
  const isTerminalStatus =
    order.status === "in_transit" ||
    order.status === "delivered" ||
    order.status === "canceled";
  const isTerminal = isTerminalStatus || isReadOnly;

  const legCount = order.leg_count;
  const isMultiLeg =
    typeof legCount === "number" ? legCount > 1 : order.leg_sequence === 0;
  // Card view is the mobile/tablet layout: multi-leg orders stay expanded so
  // every leg is visible while scrolling, with no toggle to hunt for.
  const expanded = isMultiLeg;

  const { data: childLegs, isLoading: childrenLoading } = useQuery({
    queryKey: ["order-children", order.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .eq("order_id", order.id)
        .order("leg_sequence", { ascending: true });
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
    enabled: expanded,
  });

  const resolvedLoad = resolveOrderLoadStatus({
    status: order.status,
    liveLoadStatus: order.trailer_id
      ? loadStatuses?.[order.trailer_id]?.load_status ?? null
      : null,
    historical: loadStatusAtDelivery?.get(order.id) ?? null,
  });
  const trailerLoad = resolvedLoad.value;
  const showTrailerLoadChip = !!trailerLoad;

  const readyTxt = formatWindowCompact(
    order.ready_time,
    order.ready_time_end,
    viewerTz,
    "MMM d",
    "HH:mm",
    { withTz: true },
  );
  const departed = (order as any).actual_departure_time as string | undefined;
  const eta = (order as any).eapi_eta as string | undefined;
  const deliveryTxt = order.requested_delivery_time
    ? formatWindowCompact(
        order.requested_delivery_time,
        order.requested_delivery_time_end,
        viewerTz,
        "MMM d",
        "HH:mm",
        { withTz: true },
      )
    : "—";

  // Tile visibility: render only when populated or when an inline editor is appropriate.
  const showTruckTile = !!order.assigned_tractor?.name || !isTerminal;
  // Each leg's own trailer, like the list's Trailer column ("—" for a leg without one).
  // Each leg's own trailer (legs.trailer_id via the leg summaries), like the
  // list's Trailer column ("—" for a leg without one).
  const cardLegs = order.legs ?? legs ?? [];
  const hasLegTrailer = cardLegs.some((l) => !!l.trailer_id);
  const legTrailerText = cardLegs.map((l) => l.trailer_id || "—").join(", ");
  const showTrailerTile = hasLegTrailer || !isTerminal;
  const showOperatorTile = !!order.assigned_operator?.name || !isTerminal;
  // Documents tile always available so users can upload/view from the card.
  const showDocumentsTile = true;
  const showTenderTile = !!order.tender_id;

  // Canonical short_code resolution shared across every order surface.
  const { from: parentFrom, to: parentTo } = resolveOrderEndpoints(order, locations);

  return (
    <div className="space-y-2">
      <DataCard selected={selected} onClick={() => onClick(order)}>
        {/* Header — actions left, large ID, action right; org name on its own line below */}
        <DataCard.Header>
          <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center">
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => onToggle(order.id)}
              aria-label="Select order"
            />
          </span>
          <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center">
            <StarOrderButton orderId={order.id} size="sm" />
          </span>
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span className="font-mono font-bold text-lg leading-none text-foreground truncate">
              {order.unique_id}
            </span>
            {order.is_recurring && (
              <Repeat className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            )}
          </div>
          <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {!isReadOnly && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onEdit(order)}
                aria-label="Edit order"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </span>
        </DataCard.Header>
        {order.billing_party?.organization_name && (
          <div className="text-sm text-muted-foreground truncate [font-variant-caps:all-small-caps] -mt-1">
            {order.billing_party.organization_name}
          </div>
        )}

        {/* Chips — status + trip type only */}
        <DataCard.Chips>
          {order.status === "canceled" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <OrderStatusBadge status={order.status} />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Reason: {(order as any).cancellation_reason?.trim() || "—"}
              </TooltipContent>
            </Tooltip>
          ) : (
            <OrderStatusBadge status={order.status} late={lateDep || lateArr} legs={order.legs ?? legs} />
          )}
          {order.trip_type ? (
            <Badge variant="outline" className="font-normal">{order.trip_type}</Badge>
          ) : null}
        </DataCard.Chips>

        <TrailerArrivedNotice
          legs={toLegsWithArrivedTrailer(cardLegs, order.status, (id) => loadStatuses?.[id]?.location_status)}
        />

        {/* Body — route */}
        <DataCard.Body>
          <DataCard.Field
            icon={ArrowUpRight}
            tone={lateDep ? "warning" : "default"}
            secondary={
              departed ? (
                <>
                  <div className={lateDep ? "text-[hsl(var(--aurora-status-warning))]" : undefined}>
                    Departed:{" "}
                    {formatTimeWithTz(
                      departed,
                      viewerTz,
                      "MMM d, HH:mm",
                    )}
                  </div>
                  <div className="text-muted-foreground">Scheduled: {readyTxt}</div>
                </>
              ) : (
                readyTxt
              )
            }
          >
            {parentFrom.label}
          </DataCard.Field>
          <DataCard.Field
            icon={ArrowDownRight}
            tone={lateArr ? "warning" : "default"}
            secondary={
              order.status === "in_transit" && eta ? (
                <>
                  <div className={lateArr ? "text-[hsl(var(--aurora-status-warning))]" : undefined}>
                    ETA:{" "}
                    {formatTimeWithTz(
                      eta,
                      viewerTz,
                      "MMM d, HH:mm",
                    )}
                  </div>
                  <div className="text-muted-foreground">Scheduled: {deliveryTxt}</div>
                </>
              ) : (
                deliveryTxt
              )
            }
          >
            {parentTo.label}
          </DataCard.Field>
        </DataCard.Body>

        {/* Stacked tiles */}
        <div className="flex flex-col gap-2">
          {showTruckTile && (
            <DataCard.Tile
              icon={Truck}
              label="Truck ID"
              value={
                order.assigned_tractor?.name ? (
                  order.assigned_tractor.name
                ) : (
                  <span className="text-muted-foreground font-normal">No truck assigned</span>
                )
              }
              trailing={
                order.assigned_tractor?.name || isTerminal ? undefined : (
                  <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                    <InlineAssignCell
                      orderId={order.id}
                      kind="tractor"
                      currentId={order.assigned_tractor_id ?? null}
                      currentName={order.assigned_tractor?.name ?? null}
                      readOnly={isTerminal}
                    />
                  </span>
                )
              }
            />
          )}
          {showTrailerTile && (
            <DataCard.Tile
              icon={Container}
              label="Trailer ID"
              value={
                hasLegTrailer ? (
                  legTrailerText
                ) : (
                  <span className="text-muted-foreground font-normal">No trailer assigned</span>
                )
              }
              trailing={
                hasLegTrailer || isTerminal ? undefined : (
                  <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                    <InlineEditCell
                      orderId={order.id}
                      fieldName="trailer_id"
                      currentValue={order.trailer_id}
                      placeholder="Trailer ID"
                      orderTrailerTypeId={order.trailer_type_id}
                      orderShipFromCity={order.ship_from_city}
                      readOnly={isTerminal}
                    />
                  </span>
                )
              }
              subtext={
                order.trailer_id ? (
                <span className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <InlineTrailerTypeCell
                    orderId={order.id}
                    currentValue={order.trailer_type_id}
                    fallbackValue={
                      order.trailer_id
                        ? loadStatuses?.[order.trailer_id]?.trailer_type_id ?? null
                        : null
                    }
                  />
                  {showTrailerLoadChip && (
                    <>
                      <span aria-hidden>•</span>
                      <span className="capitalize">
                        {trailerLoad}
                        {resolvedLoad.isHistorical && (
                          <span className="ml-1 text-xs text-muted-foreground">at delivery</span>
                        )}
                      </span>
                    </>
                  )}
                </span>
                ) : undefined
              }
            />
          )}
          {showOperatorTile && (
            <DataCard.Tile
              icon={User}
              label="Operator name(s)"
              value={
                order.assigned_operator?.name ? (
                  order.assigned_operator.name
                ) : (
                  <span className="text-muted-foreground font-normal">No operators assigned</span>
                )
              }
              trailing={
                order.assigned_operator?.name || isTerminal ? undefined : (
                  <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                    <InlineAssignCell
                      orderId={order.id}
                      kind="operator"
                      currentId={order.assigned_operator_id ?? null}
                      currentName={order.assigned_operator?.name ?? null}
                      readOnly={isTerminal}
                    />
                  </span>
                )
              }
            />
          )}
          {showDocumentsTile && (
            <DataCard.Tile
              icon={FileText}
              label="Documents"
              value="Documents"
              trailing={
                <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <BolViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
                  <TenderViewerDialog orderId={order.id} orderUniqueId={order.unique_id} />
                </span>
              }
            />
          )}
          {showTenderTile && (
            <DataCard.Tile
              icon={FileText}
              label="Tender ID"
              value={order.tender_id}
            />
          )}
        </div>
      </DataCard>

      {/* Child legs (read-only summary cards) */}
      {expanded && childrenLoading && (
        <div className="ml-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading legs…
        </div>
      )}
      {expanded && !childrenLoading && childLegs?.length === 0 && (
        <div className="ml-4 text-sm text-muted-foreground">No child legs found</div>
      )}
      {expanded &&
        childLegs?.map((child) => {
          const childFrom = resolveOrderEndpoints(child as any, locations).from;
          const childTo = resolveOrderEndpoints(child as any, locations).to;
          return (
          <div key={child.id} className="ml-4 border-l-2 border-muted pl-2">
            <DataCard onClick={() => onClick(child as OrderCardOrder)}>
              <DataCard.Header>
                <span className="font-mono text-sm font-medium truncate">
                  Leg {child.leg_sequence || 1} · {child.unique_id}
                </span>
                <span className="ml-auto">
                  <OrderStatusBadge status={child.status} />
                </span>
              </DataCard.Header>
              <DataCard.Body>
                <DataCard.Field icon={ArrowUpRight}>
                  {childFrom.label}
                </DataCard.Field>
                <DataCard.Field icon={ArrowDownRight}>
                  {childTo.label}
                </DataCard.Field>
              </DataCard.Body>
            </DataCard>
          </div>
          );
        })}
    </div>
  );
}